import { FC, useCallback } from 'react';
import {
  WbComponentsListArray,
  WbDivider,
  WbInfoTooltip,
  WbSelectInline,
  WbSelectInlineItem,
  WbTooltip,
} from '@agilelab/plugin-wb-platform';
import {
  Box,
  CircularProgress,
  Paper,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ErrorIcon from '@material-ui/icons/Error';
import GradientIcon from '@material-ui/icons/Gradient';
import ListAltIcon from '@material-ui/icons/ListAlt';
import { Panel } from 'reactflow';
import { System } from '../../../../types';
import { useDependencyGraphRFContext } from '../context/DependencyGraphRFContext';
import { useGetSystemScoreRange } from '../hooks/useGetSystemScoreRange';
import { ColorsMap } from '../nodes/types';

const useStyles = makeStyles(
  theme => ({
    root: {
      display: 'flex',
      gap: theme.spacing(1),
      padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px`,
      maxHeight: '250px',
      minWidth: '200px',
      overflow: 'auto',
    },
    legendColor: {
      height: '16px !important',
      width: '96px !important',
      strokeWidth: 1,
    },
    selectItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItem: 'center',
    },
    disabled: {
      opacity: 0.5,
      pointerEvents: 'none',
    },
    tooltip: {
      display: 'flex',
      alignItems: 'center',
      pointerEvents: 'auto',
    },
  }),
  { name: 'heatmap-config' },
);

export enum HeatmapKindEnum {
  NUMBER,
  DATE,
  LIST,
}

export type BaseConfig = {
  key: string;
  label: string;
  loading?: boolean;
  errors?: boolean;
  description?: string;
};

export interface HeatmapLayerConfigNumber extends BaseConfig {
  kind: HeatmapKindEnum.NUMBER;
  colors?: [string, string];
  getSystemScore: (system: System) => number | undefined;
}

export interface HeatmapLayerConfigDate extends BaseConfig {
  kind: HeatmapKindEnum.DATE;
  colors?: [string, string];
  getSystemScore: (system: System) => Date | undefined;
}

export interface HeatmapLayerConfigList extends BaseConfig {
  kind: HeatmapKindEnum.LIST;
  colors?: string[];
  getSystemScore: (system: System) => string | undefined;
}

export type HeatmapLayerConfig =
  | HeatmapLayerConfigNumber
  | HeatmapLayerConfigDate
  | HeatmapLayerConfigList;

type HeatmapConfigProps = {};

const DiscreteLegend = ({ colors }: { colors: ColorsMap }) => {
  const classes = useStyles();

  return (
    <WbComponentsListArray
      style={{ gap: 0 }}
      limit={5}
      className={classes.legendColor}
      showLegend
      iconLegendButton={<ListAltIcon />}
      popoverContent={
        <Box className={classes.root} flexDirection="column">
          <Typography variant="body1" style={{ fontWeight: 600 }}>
            Legend
          </Typography>
          {colors.map(item => (
            <Box className={classes.selectItem} key={item.value}>
              {item.value}
              <div
                style={{
                  height: '16px',
                  width: '40px',
                  backgroundColor: item.color,
                  cursor: 'default',
                }}
              />
            </Box>
          ))}
        </Box>
      }
      children={colors.map((item, index) => (
        <WbTooltip key={index} title={item.value} arrow>
          <div
            style={{
              flex: 1,
              height: '100%',
              backgroundColor: item.color,
              cursor: 'default',
            }}
          />
        </WbTooltip>
      ))}
    />
  );
};

const ContinueLegend = ({ colors }: { colors: string[] }) => {
  const classes = useStyles();

  return (
    <div
      className={classes.legendColor}
      style={{
        background: `linear-gradient(90deg, ${colors[0]} 0%, ${colors[1]} 100%)`,
      }}
    />
  );
};

export const HeatmapConfig: FC<HeatmapConfigProps> = () => {
  const classes = useStyles();
  const {
    heatmapConfig,
    defaultColors,
    heatmapLayersConfig,
    setHeatmapConfig,
  } = useDependencyGraphRFContext();

  const { systemsScores } = useGetSystemScoreRange();

  const renderLegend = useCallback(() => {
    if (systemsScores.length === 0) return 'No data';

    if (
      heatmapConfig?.kind !== HeatmapKindEnum.LIST &&
      (systemsScores as (string | number)[]).every(
        val => val === systemsScores[0],
      )
    ) {
      const colorMap = [{ value: systemsScores[0], color: '#00a0d1' }];
      return <DiscreteLegend colors={colorMap as ColorsMap} />;
    }

    switch (heatmapConfig?.kind) {
      case HeatmapKindEnum.LIST:
        return (systemsScores as ColorsMap).length > 1 ? (
          <DiscreteLegend colors={systemsScores as ColorsMap} />
        ) : (
          'No data'
        );

      case HeatmapKindEnum.NUMBER:
        return (
          <>
            <Typography variant="body2">0</Typography>
            <ContinueLegend colors={heatmapConfig.colors ?? defaultColors} />
            <Typography variant="body2">
              {Math.max(...(systemsScores as number[]))}
            </Typography>
          </>
        );

      case HeatmapKindEnum.DATE:
        return (
          <>
            <Typography variant="body2">Earliest Date</Typography>
            <ContinueLegend colors={heatmapConfig.colors ?? defaultColors} />
            <Typography variant="body2">Latest Date</Typography>
          </>
        );

      default:
        return null;
    }
  }, [
    defaultColors,
    heatmapConfig?.colors,
    heatmapConfig?.kind,
    systemsScores,
  ]);

  return (
    heatmapConfig && (
      <Panel position="bottom-center">
        <Paper>
          <Box className={classes.root} alignItems="center">
            {heatmapLayersConfig?.length === 1 ? (
              <Typography variant="body1"> {heatmapConfig.key}</Typography>
            ) : (
              <WbSelectInline
                title={
                  <Box display="flex" style={{ gap: '4px' }}>
                    <GradientIcon fontSize="small" />
                    <Typography variant="body1" style={{ fontWeight: '500' }}>
                      Heatmap
                    </Typography>
                  </Box>
                }
                value={heatmapConfig.key}
                onChange={val => {
                  setHeatmapConfig(
                    heatmapLayersConfig.find(i => i.key === val),
                  );
                }}
              >
                {...heatmapLayersConfig.map(i => (
                  <WbSelectInlineItem
                    key={i.key}
                    value={i.key}
                    disabled={i.errors || i.loading}
                    className={classes.selectItem}
                  >
                    <Typography variant="body2">{i.label}</Typography>
                    {i.loading && (
                      <WbTooltip
                        title="Data for this heatmap are loading"
                        onClick={e => e.stopPropagation()}
                      >
                        <span className={classes.tooltip}>
                          <CircularProgress size={16} />
                        </span>
                      </WbTooltip>
                    )}
                    {i.errors && (
                      <WbTooltip
                        title="An error occurred while retrieving data for this heatmap"
                        onClick={e => e.stopPropagation()}
                      >
                        <span className={classes.tooltip}>
                          <ErrorIcon />
                        </span>
                      </WbTooltip>
                    )}
                  </WbSelectInlineItem>
                ))}
              </WbSelectInline>
            )}
            {heatmapConfig.description && (
              <WbInfoTooltip title={heatmapConfig.description} />
            )}
            <WbDivider />
            {renderLegend()}
          </Box>
        </Paper>
      </Panel>
    )
  );
};
